/**
 * Client of manager-api's asynchronous screenshot operations
 * (`docs/api/screenshot-operations-v1.md`): start one operation for a batch of cameras, poll it
 * until every camera settles, and read the images of each camera from the S3 catalogue as soon as
 * it settles.
 *
 * Captures run in celery-vpn, queued one per inference machine, so a batch can take minutes: the
 * poll loop follows the server's `poll_after_ms` instead of a fixed budget.
 */

import {
  fetchScreenshotCatalog,
  fetchScreenshotOperation,
  startScreenshotOperation,
} from "../cameraPrototypes/shared/captureDeviceService";

// One operation and one catalogue page both stop at 64 cameras.
export const MAX_BATCH = 64;

const TERMINAL_STATUSES = new Set(["succeeded", "partially_succeeded", "failed", "expired"]);
const TERMINAL_ITEM_STATUSES = new Set(["succeeded", "failed", "expired"]);
const FALLBACK_POLL_MS = 3000;
const MAX_START_ATTEMPTS = 4;
const MAX_POLL_FAILURES = 3;
// An operation is tracked for 1380 s server-side; past that the poll can only 404.
const OPERATION_DEADLINE_MS = 1380 * 1000;

const ERROR_MESSAGES = {
  too_many_operations:
    "Too many screenshot requests on this store: each one is counted for up to 23 minutes. " +
    'Use "Screenshot all cameras" to capture a device in a single request.',
  camera_unavailable: "This camera is inactive or has no inference machine.",
  queue_timeout: "The capture waited too long in the queue (the inference machine is busy).",
  remote_timeout: "The inference machine did not answer in time.",
  remote_failed: "The inference machine could not capture this camera.",
  launch_unknown: "The capture could not be confirmed; the machine is protected for 10 minutes.",
  storage_failed: "The screenshot could not be stored.",
  finalization_failed: "The screenshot could not be finalized.",
  no_image_stored: "The capture succeeded but no image was found in storage.",
};

/**
 * Turn an error code (from an item or an HTTP body) into a readable message.
 *
 * @param {string|null|undefined} code - The error code.
 * @returns {string} The message; the code itself when it is unknown.
 */
export function describeScreenshotError(code) {
  return ERROR_MESSAGES[code] || code || "Screenshot failed.";
}

/**
 * Turn a failed request (start, poll or catalogue) into a readable message.
 *
 * @param {Error} error - The axios error, or any thrown error.
 * @returns {string} The message.
 */
export function describeRequestError(error) {
  const data = error?.response?.data;
  if (data?.error) return describeScreenshotError(data.error);
  return data?.detail || error?.message || "Unexpected error.";
}

function newIdempotencyKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Up to 20 % of jitter, so the rows of a "Screenshot all" burst do not poll in lockstep.
const withJitter = (ms) => ms + Math.floor(Math.random() * ms * 0.2);

function retryAfterMs(error) {
  const seconds = Number(error?.response?.headers?.["retry-after"]);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 5000;
}

async function startWithRetry(storeId, cameraPublicIds) {
  // One key for every attempt: a retry after a lost response lands on the operation it created.
  const key = newIdempotencyKey();
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await startScreenshotOperation(storeId, cameraPublicIds, key);
    } catch (error) {
      if (error?.response?.status !== 429 || attempt >= MAX_START_ATTEMPTS) throw error;
      await sleep(retryAfterMs(error));
    }
  }
}

async function pollUntilSettled(storeId, operation, onPoll) {
  const deadline = Date.now() + OPERATION_DEADLINE_MS;
  let current = operation;
  let failures = 0;
  while (!TERMINAL_STATUSES.has(current?.status)) {
    await onPoll(current);
    if (Date.now() > deadline) throw new Error("Timed out waiting for the screenshot operation.");
    await sleep(withJitter(current?.poll_after_ms || FALLBACK_POLL_MS));
    try {
      current = await fetchScreenshotOperation(storeId, operation.operation_id);
      failures = 0;
    } catch (error) {
      // A 404 means the operation is gone (expired or never ours): polling again cannot help.
      failures += 1;
      if (error?.response?.status === 404 || failures >= MAX_POLL_FAILURES) throw error;
    }
  }
  return current;
}

const urlOf = (image) => image?.url || null;

/**
 * Map one catalogue entry onto what the preview displays.
 *
 * A fisheye has a raw `complete_field` frame and one image per view; any other camera has a
 * single view, whose `pts` image carries the analysis polygon.
 *
 * @param {object} entry - `{ complete_field, views: [{ image, pts }] }` from the catalogue.
 * @returns {{ main: string|null, mainPolygons: string|null, subs: {raw: string, polygons: string|null}[] }}
 */
export function catalogEntryToImages(entry) {
  const views = entry?.views || [];
  if (entry?.complete_field) {
    return {
      main: urlOf(entry.complete_field),
      mainPolygons: null,
      subs: views.filter((view) => view.image).map((view) => ({ raw: urlOf(view.image), polygons: urlOf(view.pts) })),
    };
  }
  const [first, ...others] = views;
  return {
    main: urlOf(first?.image),
    mainPolygons: urlOf(first?.pts),
    subs: others.filter((view) => view.image).map((view) => ({ raw: urlOf(view.image), polygons: urlOf(view.pts) })),
  };
}

/**
 * Read the stored images of some cameras.
 *
 * @param {number|string} storeId - The store id.
 * @param {string[]} cameraPublicIds - At most {@link MAX_BATCH} cameras.
 * @returns {Promise<Object<string, object>>} Images per camera public id (see {@link catalogEntryToImages}).
 */
export async function loadStoredImages(storeId, cameraPublicIds) {
  const entries = await fetchScreenshotCatalog(storeId, cameraPublicIds);
  const imagesById = {};
  entries.forEach((entry) => {
    imagesById[entry.camera_public_id] = catalogEntryToImages(entry);
  });
  return imagesById;
}

async function outcomesOf(storeId, items) {
  const succeeded = items.filter((item) => item.status === "succeeded").map((item) => item.camera_public_id);
  const imagesById = succeeded.length > 0 ? await loadStoredImages(storeId, succeeded) : {};

  const outcomes = {};
  items.forEach((item) => {
    const images = imagesById[item.camera_public_id];
    if (item.status === "succeeded" && images?.main) {
      outcomes[item.camera_public_id] = { status: "success", images, refreshAllowedAt: item.refresh_allowed_at };
    } else {
      const error = item.status === "succeeded" ? "no_image_stored" : item.error || item.status;
      outcomes[item.camera_public_id] = { status: "fail", error: describeScreenshotError(error) };
    }
  });
  return outcomes;
}

/**
 * Capture a batch of cameras and return one outcome per camera.
 *
 * While the batch runs, every camera that settles is handed to `onOutcomes` right away, so a big
 * batch shows its first images without waiting for the last camera.
 *
 * @param {number|string} storeId - The store id.
 * @param {string[]} cameraPublicIds - At most {@link MAX_BATCH} active cameras with an inference
 *   machine: the server rejects the whole batch otherwise.
 * @param {(outcomes: Object<string, object>) => void} [onOutcomes] - Called with the outcomes of
 *   the cameras settled since the previous poll.
 * @returns {Promise<Object<string, object>>} Per camera public id, either
 *   `{ status: "success", images, refreshAllowedAt }` or `{ status: "fail", error }`.
 * @throws The HTTP error when the operation cannot be started or tracked.
 */
export async function captureScreenshots(storeId, cameraPublicIds, onOutcomes) {
  const outcomes = {};
  const undelivered = (operation) => (operation?.items || []).filter((item) => !(item.camera_public_id in outcomes));

  const deliverSettled = async (operation) => {
    const fresh = undelivered(operation).filter((item) => TERMINAL_ITEM_STATUSES.has(item.status));
    if (!onOutcomes || fresh.length === 0) return;
    try {
      const freshOutcomes = await outcomesOf(storeId, fresh);
      Object.assign(outcomes, freshOutcomes);
      onOutcomes(freshOutcomes);
    } catch {
      // A failed catalogue read mid-batch is retried with the next poll, or at the end.
    }
  };

  const started = await startWithRetry(storeId, cameraPublicIds);
  const settled = await pollUntilSettled(storeId, started, deliverSettled);
  // Whatever is left, settled or not, gets its outcome here, as before.
  Object.assign(outcomes, await outcomesOf(storeId, undelivered(settled)));
  return outcomes;
}
